/* eslint-disable react-hooks/rules-of-hooks */

import { useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
 const API_URL = process.env.REACT_APP_API_URL
export default function EmployerVerificationForm() {
  const [ceoPhoto, setCeoPhoto] = useState("");
  const [execPhoto, setExecPhoto] = useState("");
  const [uploading, setUploading] = useState(false);
  const [faceScore, setFaceScore] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const employerId = localStorage.getItem("userId");

  if (!employerId) {
    return (
      <div className="text-center text-red-600 font-medium">
        Please log in to verify your business.
      </div>
    );
  }

  // Upload to Cloudinary
  const uploadToCloudinary = async (file) => {
    if (!file) return null;
    const form = new FormData();
    form.append("file", file);
    form.append("upload_preset", "oosh_preset"); // Your preset

    try {
      const res = await axios.post(
        "https://api.cloudinary.com/v1_1/digzrkdoe/image/upload",
        form
      );
      return res.data.secure_url;
    } catch (err) {
      toast.error("Cloudinary upload failed");
      return null;
    }
  };

  // Handle CEO Photo
  const handleCeoPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const url = await uploadToCloudinary(file);
    if (url) {
      setCeoPhoto(url);
      toast.success("CEO photo uploaded");
    }
  };

  // Handle Executive Photo
  const handleExecPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const url = await uploadToCloudinary(file);
    if (url) {
      setExecPhoto(url);
      toast.success("Executive photo uploaded");
    }
  };

  // Submit for Face++ Verification
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ceoPhoto || !execPhoto) return toast.error("Upload both photos");

    setIsVerifying(true);
    setUploading(true);

    try {
      const res = await axios.post(`${API_URL}verification/create`, {
        jobEmployerId: employerId,
        ceoPhoto,
        executivePhoto: execPhoto,
      });

      const score = res.data.data?.faceMatchScore;
      setFaceScore(score);

      if (res.data.data?.isVerified) {
        toast.success(`Verified! Match: ${score}%`);
      } else {
        toast.error(`Face match failed: ${score}% (Need ≥80%)`);
      }
    } catch (err) {
      console.error("Verification error:", err);
      toast.error(err.response?.data?.message || "Verification failed");
    } finally {
      setUploading(false);
      setIsVerifying(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
        Business Face Verification
      </h2>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* CEO Photo */}
        <div className="space-y-3">
          <label className="block text-lg font-semibold text-gray-700">
            CEO Photo
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleCeoPhoto}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {ceoPhoto && (
            <div className="mt-3">
              <img
                src={ceoPhoto}
                alt="CEO"
                className="w-40 h-40 object-cover rounded-lg shadow-md border"
              />
              <p className="text-xs text-green-600 mt-1">Uploaded</p>
            </div>
          )}
        </div>

        {/* Executive Photo */}
        <div className="space-y-3">
          <label className="block text-lg font-semibold text-gray-700">
            Executive Photo
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleExecPhoto}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
          />
          {execPhoto && (
            <div className="mt-3">
              <img
                src={execPhoto}
                alt="Executive"
                className="w-40 h-40 object-cover rounded-lg shadow-md border"
              />
              <p className="text-xs text-green-600 mt-1">Uploaded</p>
            </div>
          )}
        </div>

        {/* Face Match Score */}
        {faceScore !== null && (
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg text-center">
            <p className="text-sm font-medium text-gray-700">Face Match Score</p>
            <p className={`text-3xl font-bold ${faceScore >= 80 ? "text-green-600" : "text-red-600"}`}>
              {faceScore}%
            </p>
            <p className="text-xs mt-1">
              {faceScore >= 80 ? "Verification Passed" : "Need ≥80%"}
            </p>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={uploading || !ceoPhoto || !execPhoto}
          className={`w-full py-4 rounded-lg font-bold text-white transition-all transform
            ${uploading || !ceoPhoto || !execPhoto
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:scale-105 shadow-lg"
            }`}
        >
          {isVerifying ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Verifying Faces...
            </span>
          ) : (
            "Submit for Face Verification"
          )}
        </button>
      </form>

      {/* Info */}
      <div className="mt-6 text-center text-xs text-gray-500">
        <p>Verification valid for <strong>3 months</strong></p>
        <p className="mt-1">Photos must clearly show faces</p>
      </div>
    </div>
  );
}










































